import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, ChevronRight, BookOpen, Plus, Trash2, X, Loader2, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { wellnessResources } from '../data/resources';
import { VipSwitcher } from './CoachingPage';
import { VipSearchBar, matchItems } from '../components/VipSearch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAdminAuth = () => {
  try { return JSON.parse(sessionStorage.getItem('gw_admin_auth') || 'null'); } catch { return null; }
};

export default function ReadingRoomPage() {
  const { user, hasAccess, loading } = useAuth();
  const navigate = useNavigate();
  const admin = getAdminAuth();

  const [books, setBooks] = useState([]);
  const [activeBook, setActiveBook] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ title: '', author: '', description: '', file: null });
  const [query, setQuery] = useState('');
  const searching = query.trim().length > 0;
  const shownArticles = useMemo(() => matchItems(
    wellnessResources.map((a) => ({ ...a, haystack: [a.title, a.description, a.type, ...(a.topics || [])].join(' ').toLowerCase() })), query,
  ), [query]);
  const shownBooks = useMemo(() => matchItems(
    books.map((b) => ({ ...b, haystack: [b.title, b.author, b.description].join(' ').toLowerCase() })), query,
  ), [books, query]);

  const loadBooks = async () => {
    try {
      const res = await fetch(`${API}/vip/books`);
      if (res.ok) setBooks((await res.json()).books || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadBooks(); }, []);

  // Lock background scroll while reading
  useEffect(() => {
    document.body.style.overflow = activeBook ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [activeBook]);

  const submitBook = async (e) => {
    e.preventDefault();
    if (!admin || !form.file || !form.title.trim()) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', form.file);
      fd.append('title', form.title);
      fd.append('author', form.author);
      fd.append('description', form.description);
      const res = await fetch(
        `${API}/vip/books?admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`,
        { method: 'POST', body: fd }
      );
      if (res.ok) {
        setShowAdd(false);
        setForm({ title: '', author: '', description: '', file: null });
        await loadBooks();
      } else {
        const msg = res.status === 413 ? 'That PDF is too large (max 100 MB).'
          : res.status === 400 ? 'Please choose a PDF file.'
          : 'Upload failed — please try again.';
        alert(msg);
      }
    } catch {
      alert('Upload failed — please check your connection and try again.');
    } finally {
      setUploading(false);
    }
  };

  const deleteBook = async (id) => {
    if (!admin) return;
    if (!window.confirm('Remove this book from the Reading Room?')) return;
    const res = await fetch(
      `${API}/vip/books/${id}?admin_email=${encodeURIComponent(admin.email)}&admin_password=${encodeURIComponent(admin.password)}`,
      { method: 'DELETE' }
    );
    if (res.ok) setBooks((prev) => prev.filter((b) => b.id !== id));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-stone-500 font-body">Loading…</div>
      </div>
    );
  }

  // Access gate — VIP members (or Jill in admin mode) only
  if ((!user || !hasAccess('vip')) && !admin) {
    return (
      <div className="min-h-screen bg-cream py-16 px-4" data-testid="reading-room-gate">
        <div className="max-w-2xl mx-auto text-center">
          <Link to="/programs" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-10" data-testid="reading-room-back">
            <ArrowLeft size={16} /> Back to Programs
          </Link>
          <div className="bg-[#f5efe4] border border-stone-200 rounded-2xl p-10 md:p-14">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-forest/10 text-forest mb-6">
              <Lock size={26} />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl text-forest mb-4">The Reading Room</h1>
            <p className="text-stone-600 font-body leading-relaxed mb-8">
              A private library of readings and resources for V.I.P. members. Please log in with your V.I.P. account to enter.
            </p>
            <button onClick={() => navigate('/programs')} className="inline-flex items-center gap-2 bg-forest text-cream font-body font-medium px-6 py-3 rounded-full hover:bg-forest-dark transition-colors" data-testid="reading-room-login">
              Go to Programs to log in <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4" data-testid="reading-room-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/programs/vip" className="inline-flex items-center gap-2 text-forest/70 hover:text-forest font-body text-sm mb-8">
          <ArrowLeft size={16} /> Back to V.I.P.
        </Link>

        <VipSwitcher active="reading-room" />

        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mb-10">
          <div className="flex items-center gap-2 mb-3">
            <Crown size={16} className="text-[#8a6a5a]" />
            <p className="text-xs font-body text-[#8a6a5a] uppercase tracking-[0.25em]">V.I.P. · The Reading Room</p>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl text-forest mb-3">The Reading Room</h1>
          <p className="text-stone-600 font-body max-w-2xl">
            A quiet corner of readings and resources to explore at your own pace — browse the articles, or open a book or PDF to read it right here.
          </p>
        </motion.div>

        <VipSearchBar
          value={query}
          onChange={setQuery}
          placeholder="Search articles, books & PDFs — try “sleep”, “stress” or “mindset”"
          testId="reading-room-search"
          className="mb-10"
        />
        {searching && (
          <p className="font-body text-sm text-stone-500 -mt-6 mb-8" data-testid="reading-room-search-count">
            {shownArticles.length + shownBooks.length
              ? `${shownArticles.length} article${shownArticles.length === 1 ? '' : 's'} and ${shownBooks.length} book${shownBooks.length === 1 ? '' : 's'} for “${query.trim()}”`
              : `Nothing found for “${query.trim()}” yet — try a different word.`}
          </p>
        )}

        {/* Articles — browsable list of all site articles */}
        {(!searching || shownArticles.length > 0) && (
        <section className="mb-14" data-testid="reading-room-articles">
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-1">Articles</h2>
          <p className="font-body text-sm text-stone-500 mb-5">All of Jill's writings — tap any one to read the full article.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {shownArticles.map((a) => (
              <Link
                key={a.id}
                to={`/resources/wellness?resource=${a.id}`}
                className="group bg-white border border-stone-200 rounded-2xl overflow-hidden hover:border-[#8a6a5a]/50 hover:shadow-md transition-all flex flex-col"
                data-testid={`article-card-${a.id}`}
              >
                {a.image && (
                  <div className="aspect-[16/9] overflow-hidden bg-stone-100">
                    <img src={a.image} alt={a.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                )}
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="font-heading text-lg text-forest mb-1 leading-snug">{a.title}</h3>
                  {a.description && <p className="font-body text-sm text-stone-500 leading-relaxed line-clamp-3 flex-1">{a.description}</p>}
                  <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-body text-[#8a6a5a] group-hover:gap-2.5 transition-all">
                    Read article <ChevronRight size={15} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
        )}

        {/* Books & PDFs */}
        {(!searching || shownBooks.length > 0) && (
        <section data-testid="reading-room-books">
          <h2 className="font-heading text-2xl md:text-3xl text-forest mb-1">Books &amp; PDFs</h2>
          <p className="font-body text-sm text-stone-500 mb-5">Books, worksheets and guides to read right here.</p>

        {admin && (
          <div className="mb-8">
            <button
              onClick={() => setShowAdd(true)}
              className="inline-flex items-center gap-2 bg-forest text-cream font-body text-sm px-5 py-2.5 rounded-full hover:bg-forest-dark transition-colors"
              data-testid="reading-room-add-btn"
            >
              <Plus size={16} /> Add a book
            </button>
          </div>
        )}

        {books.length === 0 ? (
          <div className="bg-white border border-stone-200 rounded-2xl p-12 text-center" data-testid="reading-room-empty">
            <BookOpen size={32} className="text-stone-300 mx-auto mb-4" />
            <p className="font-body text-stone-500">
              {admin ? 'No books yet — add your first one above.' : 'No books or PDFs added yet — check back soon.'}
            </p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="reading-room-grid">
            {shownBooks.map((b) => (
              <div
                key={b.id}
                className="group bg-white border border-stone-200 rounded-2xl p-6 hover:border-[#8a6a5a]/50 hover:shadow-md transition-all flex flex-col"
                data-testid={`book-card-${b.id}`}
              >
                <div className="flex items-start justify-between gap-2 mb-4">
                  <div className="w-11 h-11 rounded-xl bg-[#f3ece0] flex items-center justify-center flex-shrink-0">
                    <BookOpen size={20} className="text-[#8a6a5a]" />
                  </div>
                  {admin && (
                    <button onClick={() => deleteBook(b.id)} className="text-stone-300 hover:text-red-600 transition-colors" title="Remove book" data-testid={`book-delete-${b.id}`}>
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                <h3 className="font-heading text-xl text-forest mb-1 leading-snug">{b.title}</h3>
                {b.author && <p className="font-body text-sm text-[#8a6a5a] mb-2">{b.author}</p>}
                {b.description && <p className="font-body text-sm text-stone-500 leading-relaxed flex-1 mb-4">{b.description}</p>}
                <button
                  onClick={() => setActiveBook(b)}
                  className="mt-auto inline-flex items-center justify-center gap-2 bg-forest/5 border border-forest/20 text-forest font-body text-sm px-4 py-2.5 rounded-full hover:bg-forest/10 transition-colors"
                  data-testid={`book-read-${b.id}`}
                >
                  <BookOpen size={15} /> Read now
                </button>
              </div>
            ))}
          </div>
        )}
        </section>
        )}
      </div>

      {/* Reader modal */}
      <AnimatePresence>
        {activeBook && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3 sm:p-6"
            onClick={() => setActiveBook(null)}
            data-testid="book-reader-modal"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-4xl h-[90vh] flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-stone-200 flex-shrink-0">
                <div className="min-w-0">
                  <p className="font-heading text-lg text-forest truncate">{activeBook.title}</p>
                  {activeBook.author && <p className="font-body text-xs text-[#8a6a5a] truncate">{activeBook.author}</p>}
                </div>
                <button onClick={() => setActiveBook(null)} className="text-stone-400 hover:text-forest flex-shrink-0" data-testid="book-reader-close">
                  <X size={22} />
                </button>
              </div>
              <div className="flex-1 bg-stone-100">
                <iframe
                  title={activeBook.title}
                  src={`${API}/vip/books/${activeBook.id}/file#toolbar=0&navpanes=0`}
                  className="w-full h-full"
                  data-testid="book-reader-iframe"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Admin: add book modal */}
      <AnimatePresence>
        {showAdd && admin && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
            onClick={() => !uploading && setShowAdd(false)}
            data-testid="add-book-modal"
          >
            <motion.div
              initial={{ scale: 0.97, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.97, opacity: 0 }}
              className="bg-white rounded-2xl w-full max-w-md p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-heading text-xl text-forest">Add a book</h3>
                <button onClick={() => !uploading && setShowAdd(false)} className="text-stone-400 hover:text-forest"><X size={20} /></button>
              </div>
              <form onSubmit={submitBook} className="space-y-4">
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Title *</label>
                  <input
                    type="text" required value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest"
                    data-testid="add-book-title"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Author</label>
                  <input
                    type="text" value={form.author}
                    onChange={(e) => setForm({ ...form, author: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest"
                    data-testid="add-book-author"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">Short description</label>
                  <textarea
                    rows={3} value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full border border-stone-300 rounded-lg px-3 py-2 font-body text-sm focus:outline-none focus:border-forest resize-none"
                    data-testid="add-book-description"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-stone-600 block mb-1">PDF file * (max 100 MB)</label>
                  <input
                    type="file" accept="application/pdf,.pdf" required
                    onChange={(e) => setForm({ ...form, file: e.target.files?.[0] || null })}
                    className="w-full font-body text-sm"
                    data-testid="add-book-file"
                  />
                </div>
                <button
                  type="submit" disabled={uploading || !form.file || !form.title.trim()}
                  className="w-full inline-flex items-center justify-center gap-2 bg-forest text-cream font-body font-medium px-5 py-3 rounded-full hover:bg-forest-dark transition-colors disabled:opacity-50"
                  data-testid="add-book-submit"
                >
                  {uploading ? (<><Loader2 size={16} className="animate-spin" /> Uploading…</>) : (<><Plus size={16} /> Add to Reading Room</>)}
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
